import { BaseClient } from '../base-client';
import type {
  CreateReleaseNoteInput,
  ListReleaseNotesQuery,
  PatchReleaseNoteInput,
  ReleaseNote,
} from './types';

type One = { data: ReleaseNote };
type ListResponse = { data: ReleaseNote[]; meta: { total: number; limit: number; offset: number } };

export class ReleaseNotesClient extends BaseClient {
  list(query: ListReleaseNotesQuery = {}): Promise<ListResponse> {
    return this.request('/api/release-notes', { query: normalize(query) });
  }

  /** Batch fetch release notes by exactly the referenced ids. See `BaseClient.findByIdsAt`. */
  findByIds(ids: readonly string[]): Promise<ListResponse> {
    return this.findByIdsAt<ReleaseNote>('/api/release-notes/find', ids);
  }

  get(id: string): Promise<One> {
    return this.request(`/api/release-notes/${encodeURIComponent(id)}`);
  }

  create(input: CreateReleaseNoteInput): Promise<One> {
    return this.request('/api/release-notes', { method: 'POST', body: input });
  }

  patch(id: string, input: PatchReleaseNoteInput): Promise<One> {
    return this.request(`/api/release-notes/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  }

  remove(id: string): Promise<One> {
    return this.request(`/api/release-notes/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: {},
    });
  }

  restore(id: string): Promise<One> {
    return this.request(`/api/release-notes/${encodeURIComponent(id)}/restore`, {
      method: 'POST',
      body: {},
    });
  }
}

function normalize(q: object): Record<string, string | number | boolean | undefined> {
  const out: Record<string, string | number | boolean | undefined> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === '') continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    }
  }
  return out;
}
